import { lazy, Suspense, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Breadcrumbs,
  Button,
  Card,
  Code,
  Group,
  Loader,
  Progress,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  IconCpu,
  IconStack2,
  IconActivity,
  IconDatabase,
  IconTerminal2,
  IconDisc,
  IconAdjustments,
  IconHistory,
  IconPlayerPlay,
  IconPower,
  IconPlayerStop,
  IconPlayerPause,
  IconTrash,
  IconAlertTriangle,
  IconNetwork,
  IconInfoCircle,
  IconPlus,
} from "@tabler/icons-react";
import { apiDelete, apiFetch, friendlyError, type ApiError } from "../../api/client.js";
import {
  formatBytes,
  formatRam,
  formatUptime,
  isVmDataDisk,
  vmAction,
  type VmAction,
  type VmDetail,
  type VmDisk,
  type GuestInterface,
} from "./vmUtils.js";
import { StatusPill } from "../../components/StatusPill.js";
import { ConsoleModal } from "./ConsoleModal.js";
import { IsoPickerModal } from "./IsoPickerModal.js";
import { ResizeVmModal } from "./ResizeVmModal.js";
import { SnapshotsModal } from "./SnapshotsModal.js";
import { AddDiskModal } from "./AddDiskModal.js";
import { BackupsCard } from "./BackupsCard.js";

const POLL_MS = 5000;

// Lazily loaded so recharts/@mantine/charts stays out of the main bundle.
const DiskActivityCard = lazy(() => import("./DiskActivityCard.js"));

export function VmDetail() {
  const { vmid } = useParams<{ vmid: string }>();
  const navigate = useNavigate();
  const [vm, setVm] = useState<VmDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [consoleOpen, setConsoleOpen] = useState(false);
  const [isoOpen, setIsoOpen] = useState(false);
  const [resizeOpen, setResizeOpen] = useState(false);
  const [snapshotsOpen, setSnapshotsOpen] = useState(false);
  const [addDiskOpen, setAddDiskOpen] = useState(false);
  const [busyDiskSlot, setBusyDiskSlot] = useState<string | null>(null);

  async function load() {
    if (!vmid) return;
    try {
      const data = await apiFetch<VmDetail>(`/api/vms/${vmid}`);
      setVm(data);
      setError(null);
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr.status === 502 && apiErr.error === "pve_unavailable") {
        setError("Can't reach Proxmox right now — retrying…");
      } else {
        setError(friendlyError(err, "Could not load VM."));
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vmid]);

  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vmid]);

  async function runAction(action: VmAction, body?: { force?: boolean; timeout?: number }) {
    if (!vm) return;
    setBusy(true);
    try {
      const res = await vmAction(vm.vmid, action, body);
      notifications.show({ color: res.ok ? "green" : "red", message: res.message });
      if (res.ok) void load();
    } finally {
      setBusy(false);
    }
  }

  function confirmPower(action: "shutdown" | "stop") {
    if (!vm) return;
    const isStop = action === "stop";
    modals.openConfirmModal({
      title: isStop ? "Force stop VM" : "Shut down VM",
      centered: true,
      children: (
        <Text size="sm">
          {isStop ? (
            <>
              Force stop <b>{vm.name}</b>? This pulls the power and may corrupt the filesystem.
            </>
          ) : (
            <>
              Shut down <b>{vm.name}</b>? An ACPI shutdown is sent; the guest may take a while.
            </>
          )}
        </Text>
      ),
      labels: { confirm: isStop ? "Force stop" : "Shut down", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: () => void runAction(action, isStop ? undefined : { force: false }),
    });
  }

  async function deleteVm() {
    if (!vm) return;
    setBusy(true);
    try {
      await apiDelete(`/api/vms/${vm.vmid}`);
      notifications.show({ color: "green", message: "VM deleted." });
      navigate("/dashboard");
    } catch (err) {
      // Surface backend's human message when present (e.g. stop_timeout's
      // "VM did not stop within 30s. Try again in a moment.").
      const detail = (err as ApiError)?.details as { message?: string } | undefined;
      notifications.show({
        color: "red",
        message: detail?.message ?? "Could not delete VM. Try again or contact support.",
      });
    } finally {
      setBusy(false);
    }
  }

  function confirmDelete() {
    if (!vm) return;
    modals.openConfirmModal({
      title: "Delete VM",
      centered: true,
      closeOnClickOutside: false,
      closeOnEscape: false,
      children: (
        <Text size="sm">
          Permanently delete <b>{vm.name}</b> (VM ID {vm.vmid})? This destroys the VM and all its
          data. <b>This cannot be undone.</b>
        </Text>
      ),
    labels: { confirm: "Delete VM", cancel: "Cancel" },
    confirmProps: { color: "red" },
    groupProps: { gap: "xs" },
    onConfirm: () => void deleteVm(),
  });
}

async function deleteDisk(slot: string) {
  if (!vm) return;
  setBusyDiskSlot(slot);
  try {
    await apiDelete(`/api/vms/${vm.vmid}/disks/${encodeURIComponent(slot)}`);
    notifications.show({ color: "green", message: `Disk ${slot} deleted.` });
    void load();
  } catch (err) {
    const e = err as ApiError;
    let message: string;
    if (e.status === 409 && e.error === "boot_disk_protected") {
      message = "The boot disk cannot be deleted.";
    } else if (e.status === 404 && e.error === "disk_not_found") {
      message = "This disk no longer exists.";
    } else if (e.status === 409 && e.error === "vm_locked") {
      message = "Another operation is in progress; try again in a moment.";
    } else if (e.status === 502 && e.error === "pve_forbidden") {
      message = "Proxmox denied the disk operation. Contact support.";
    } else if (e.status === 502 && e.error === "pve_unavailable") {
      message = "Proxmox is unavailable. Please try again.";
    } else {
      message = friendlyError(e, "Couldn't delete disk.");
    }
    notifications.show({ color: "red", message });
    if (e.status === 404) void load();
  } finally {
    setBusyDiskSlot(null);
  }
}

function confirmDeleteDisk(disk: VmDisk) {
  if (!vm) return;
  modals.openConfirmModal({
    title: "Delete disk",
    centered: true,
    children: (
      <Text size="sm">
        Permanently delete disk <Code>{disk.slot}</Code> ({formatBytes(disk.size_bytes)}) from{" "}
        <b>{vm.name}</b>? <b>All data on this disk will be permanently destroyed.</b> This cannot
        be undone.
      </Text>
    ),
    labels: { confirm: "Delete disk", cancel: "Cancel" },
    confirmProps: { color: "red" },
    groupProps: { gap: "xs" },
    onConfirm: () => void deleteDisk(disk.slot),
  });
}

  if (loading && !vm) {
    return (
      <Stack gap="xs" py="xl" align="center">
        <Loader />
        <Text c="dimmed" size="sm">
          Loading…
        </Text>
      </Stack>
    );
  }
  if (error && !vm) {
    return (
      <Stack p="md" gap="md">
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />}>
          {error}
        </Alert>
        <div>
          <Button variant="default" size="xs" w={140} onClick={() => navigate("/dashboard")}>
            Back to list
          </Button>
        </div>
      </Stack>
    );
  }
  if (!vm) return null;

  // Phase 31: mounted-ISO state comes from the backend's customer-safe
  // mounted_iso field (opaque id + display name) — the disks array no
  // longer carries cdrom volume identity to reconstruct it from.
  const mountedIso = vm.mounted_iso ?? null;
  const dataDisks = vm.disks.filter(isVmDataDisk);
  const cpuUsagePct = vm.cpu_usage_fraction > 0 ? Math.round(vm.cpu_usage_fraction * 100) : null;
  // Guest-agent memory reports against the real in-guest MemTotal; the
  // host-side fallback reports against the configured memory_mb.
  const memTotalBytes =
    vm.memory_source === "guest"
      ? vm.memory_total_bytes ?? 0
      : vm.memory_mb * 1024 * 1024;
  const memUsagePct =
    vm.memory_usage_bytes > 0 && memTotalBytes > 0
      ? Math.min(100, Math.round((vm.memory_usage_bytes / memTotalBytes) * 100))
      : null;
  const memUsageSecondary =
    vm.memory_usage_bytes > 0
      ? `${formatBytes(vm.memory_usage_bytes)} in use${
          (vm.memory_source ?? "host") === "host" ? " (incl. disk cache)" : ""
        }`
      : null;
  const swapSecondary =
    (vm.swap_total_bytes ?? 0) > 0
      ? `Swap: ${
          (vm.swap_usage_bytes ?? 0) > 0 ? formatBytes(vm.swap_usage_bytes ?? 0) : "0 B"
        } of ${formatBytes(vm.swap_total_bytes ?? 0)} used`
      : null;

  return (
    <Stack gap="md">
      <Breadcrumbs>
        <Anchor component={Link} to="/dashboard" size="sm">
          Virtual Machines
        </Anchor>
        <Text size="sm" fw={600}>
          {vm.name}
        </Text>
      </Breadcrumbs>

      {/* Header card: identity + primary actions */}
      <Card padding="lg">
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
          <Stack gap={4}>
            <Group gap="sm" align="center">
              <Title order={2}>{vm.name}</Title>
              <StatusPill status={vm.status} />
              {mountedIso && (
                <Badge color="blue" variant="light" leftSection={<IconDisc size={12} />}>
                  ISO mounted
                </Badge>
              )}
              {vm.snapshot_count > 0 && (
                <Tooltip
                  label={`This VM has ${vm.snapshot_count} snapshot${vm.snapshot_count === 1 ? "" : "s"}. Snapshots are protective but can slow disk I/O if many accumulate. Up to 5 snapshots can be kept — review periodically and remove unneeded ones.`}
                  position="bottom"
                  withArrow
                  multiline
                  w={260}
                >
                  <Badge
                    size="sm"
                    variant="light"
                    color={vm.snapshot_count >= 5 ? "red" : vm.snapshot_count >= 3 ? "yellow" : "gray"}
                    leftSection={<IconHistory size={12} stroke={1.75} />}
                    styles={{ root: { cursor: "default" } }}
                  >
                    {vm.snapshot_count} snapshot{vm.snapshot_count === 1 ? "" : "s"}
                  </Badge>
                </Tooltip>
              )}
            </Group>
            <Group gap="xs">
              <Text size="xs" c="dimmed">
                VM ID <Text span ff="monospace" fw={600}>{vm.vmid}</Text>
              </Text>
              <Text size="xs" c="dimmed">
                · Node <Text span ff="monospace" fw={600}>{vm.node}</Text>
              </Text>
              <Text size="xs" c="dimmed">
                · Uptime <Text span fw={600}>{formatUptime(vm.uptime_seconds)}</Text>
              </Text>
            </Group>
          </Stack>

          <Group gap="xs" wrap="wrap">
            {vm.status === "stopped" && (
              <Tooltip label="Start" position="bottom" withArrow>
                <ActionIcon
                  size="lg"
                  variant="filled"
                  color="blue"
                  loading={busy}
                  onClick={() => void runAction("start")}
                  aria-label="Start"
                >
                  <IconPlayerPlay size={18} stroke={1.75} />
                </ActionIcon>
              </Tooltip>
            )}
            {vm.status === "running" && (
              <>
                <Tooltip label="Shutdown" position="bottom" withArrow>
                  <ActionIcon
                    size="lg"
                    variant="default"
                    loading={busy}
                    onClick={() => confirmPower("shutdown")}
                    aria-label="Shutdown"
                  >
                    <IconPower size={18} stroke={1.75} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Stop (force)" position="bottom" withArrow>
                  <ActionIcon
                    size="lg"
                    variant="light"
                    color="red"
                    loading={busy}
                    onClick={() => confirmPower("stop")}
                    aria-label="Stop (force)"
                  >
                    <IconPlayerStop size={18} stroke={1.75} />
                  </ActionIcon>
                </Tooltip>
              </>
            )}
            {vm.status === "paused" && (
              <Tooltip label="Resume is not supported yet" position="bottom" withArrow>
                <ActionIcon size="lg" variant="default" disabled aria-label="Resume is not supported yet">
                  <IconPlayerPause size={18} stroke={1.75} />
                </ActionIcon>
              </Tooltip>
            )}
            {vm.status === "running" ? (
              <Tooltip label="Console" position="bottom" withArrow>
                <ActionIcon
                  size="lg"
                  variant="default"
                  onClick={() => setConsoleOpen(true)}
                  aria-label="Console"
                >
                  <IconTerminal2 size={18} stroke={1.75} />
                </ActionIcon>
              </Tooltip>
            ) : (
              <Tooltip label="Console is only available when the VM is running" position="bottom" withArrow>
                <ActionIcon size="lg" variant="default" disabled aria-label="Console is only available when the VM is running">
                  <IconTerminal2 size={18} stroke={1.75} />
                </ActionIcon>
              </Tooltip>
            )}
            <Tooltip label="Mount ISO" position="bottom" withArrow>
              <ActionIcon size="lg" variant="default" onClick={() => setIsoOpen(true)} aria-label="Mount ISO">
                <IconDisc size={18} stroke={1.75} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Resize" position="bottom" withArrow>
              <ActionIcon size="lg" variant="default" onClick={() => setResizeOpen(true)} aria-label="Resize">
                <IconAdjustments size={18} stroke={1.75} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Snapshots" position="bottom" withArrow>
              <ActionIcon
                size="lg"
                variant="default"
                onClick={() => setSnapshotsOpen(true)}
                aria-label="Snapshots"
              >
                <IconHistory size={18} stroke={1.75} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Delete" position="bottom" withArrow>
              <ActionIcon
                size="lg"
                variant="light"
                color="red"
                loading={busy}
                onClick={confirmDelete}
                aria-label="Delete"
              >
                <IconTrash size={18} stroke={1.75} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </Card>

      {error && (
        <Alert color="orange" variant="light" icon={<IconAlertTriangle size={18} />}>
          {error} (showing last known state)
        </Alert>
      )}

      {/* Resource cards */}
      <SimpleGrid cols={{ base: 1, md: 3 }}>
        <ResourceCard
          icon={<IconCpu size={18} stroke={1.75} />}
          label="CPU"
          primary={`${vm.cpu_cores || "—"} cores`}
          secondary={`${vm.sockets || 1} socket${(vm.sockets || 1) > 1 ? "s" : ""}`}
          usagePct={cpuUsagePct}
          usageLabel={cpuUsagePct !== null ? `${cpuUsagePct}% in use` : null}
          usageColor="brand"
        />
        <ResourceCard
          icon={<IconStack2 size={18} stroke={1.75} />}
          label="Memory"
          primary={vm.memory_mb ? formatRam(vm.memory_mb) : "—"}
          secondary={
            memUsageSecondary || swapSecondary ? (
              <>
                {memUsageSecondary && (
                  <Text span size="sm" c="dimmed" style={{ display: "block" }}>
                    {memUsageSecondary}
                  </Text>
                )}
                {swapSecondary && (
                  <Text span size="sm" c="dimmed" style={{ display: "block" }}>
                    {swapSecondary}
                  </Text>
                )}
              </>
            ) : null
          }
          usagePct={memUsagePct}
          usageLabel={memUsagePct !== null ? `${memUsagePct}% in use` : null}
          usageColor="accent"
        />
        <ResourceCard
          icon={<IconActivity size={18} stroke={1.75} />}
          label="Status"
          primary={<span style={{ textTransform: "capitalize" }}>{vm.qmpstatus || vm.status}</span>}
          secondary={`uptime ${formatUptime(vm.uptime_seconds)}`}
        />
      </SimpleGrid>

      {/* Disk activity (Phase 35) */}
      <Suspense fallback={null}>
        <DiskActivityCard kind="qemu" vmid={vm.vmid} />
      </Suspense>

      {/* Network */}
      <Card padding="lg">
        <Stack gap="sm">
          <Group gap="xs">
            <IconNetwork size={18} stroke={1.75} />
            <Title order={3}>Network</Title>
          </Group>
          {vm.ip_status === "agent_disabled" && (
            <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
              Install <Code>qemu-guest-agent</Code> inside this VM and restart it to see its IP address.
            </Alert>
          )}
          {vm.ip_status === "agent_not_running" && (
            <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
              Guest agent isn't responding. If you've installed <Code>qemu-guest-agent</Code>, make sure the service is running inside the VM and the VM has been restarted.
            </Alert>
          )}
          {vm.ip_status === "vm_stopped" && (
            <Alert color="gray" variant="light" icon={<IconInfoCircle size={16} />}>
              Start the VM to see its IP address.
            </Alert>
          )}
          {vm.ip_status === "unreachable" && (
            <Alert color="orange" variant="light" icon={<IconInfoCircle size={16} />}>
              Couldn't read network info from Proxmox right now.
            </Alert>
          )}
          {vm.ip_status === "ok" && vm.ip_addresses.length === 0 && (
            <Text c="dimmed" size="sm">
              No IP address assigned yet — the VM may still be booting.
            </Text>
          )}
          {vm.ip_status === "ok" && vm.ip_addresses.length > 0 && (
            <Table.ScrollContainer minWidth={500}>
              <Table withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Interface</Table.Th>
                    <Table.Th>MAC</Table.Th>
                    <Table.Th>IPv4</Table.Th>
                    <Table.Th>IPv6</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {vm.ip_addresses.map((iface: GuestInterface) => (
                    <Table.Tr key={iface.name}>
                      <Table.Td>{iface.name}</Table.Td>
                      <Table.Td ff="monospace">{iface.mac || "—"}</Table.Td>
                      <Table.Td ff="monospace">{iface.ipv4 || "—"}</Table.Td>
                      <Table.Td ff="monospace">{iface.ipv6 || "—"}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Stack>
      </Card>

      {/* Disks */}
      <Card padding="lg">
        <Stack gap="sm">
          <Group gap="xs">
            <IconDatabase size={18} stroke={1.75} />
            <Title order={3}>Disks</Title>
            <Badge variant="light" color="gray">
              {formatBytes(vm.disk_total_bytes)}
            </Badge>
            <Tooltip label="Add disk" position="bottom" withArrow>
              <ActionIcon
                size="md"
                variant="default"
                ml="auto"
                onClick={() => setAddDiskOpen(true)}
                aria-label="Add disk"
              >
                <IconPlus size={16} stroke={1.75} />
              </ActionIcon>
            </Tooltip>
          </Group>
          {dataDisks.length === 0 ? (
            <Text c="dimmed" size="sm">
              No disk information.
            </Text>
          ) : (
            <Table.ScrollContainer minWidth={500}>
              <Table withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Slot</Table.Th>
                    <Table.Th>Size</Table.Th>
                    <Table.Th>Format</Table.Th>
                    <Table.Th w={60}></Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {dataDisks.map((d) => {
                    const isBoot = d.slot === vm.boot_slot;
                    return (
                      <Table.Tr key={d.slot}>
                        <Table.Td ff="monospace">
                          <Group gap="xs" wrap="nowrap">
                            {d.slot}
                            {isBoot && (
                              <Badge variant="light" color="gray" size="sm">
                                boot
                              </Badge>
                            )}
                          </Group>
                        </Table.Td>
                        <Table.Td>{formatBytes(d.size_bytes)}</Table.Td>
                        <Table.Td>{d.format ?? "—"}</Table.Td>
                        <Table.Td>
                          {!isBoot && (
                            <Tooltip label="Delete disk" position="left" withArrow>
                              <ActionIcon
                                size="md"
                                variant="light"
                                color="red"
                                loading={busyDiskSlot === d.slot}
                                disabled={busyDiskSlot !== null && busyDiskSlot !== d.slot}
                                onClick={() => confirmDeleteDisk(d)}
                                aria-label={`Delete disk ${d.slot}`}
                              >
                                <IconTrash size={15} stroke={1.75} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Stack>
      </Card>

      {/* Backups */}
      <BackupsCard kind="qemu" vmid={vm.vmid} name={vm.name} />

      {/* Metadata */}
      <Card padding="lg">
        <SimpleGrid cols={{ base: 1, md: 2 }}>
          <Stack gap="xs">
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              OS type
            </Text>
            <Text>{vm.ostype ?? "—"}</Text>
          </Stack>
          <Stack gap="xs">
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              Tags
            </Text>
            <Group gap="xs">
              {vm.tags ? (
                vm.tags
                  .split(";")
                  .filter(Boolean)
                  .map((t) => (
                    <Badge key={t} variant="light">
                      {t}
                    </Badge>
                  ))
              ) : (
                <Text c="dimmed">—</Text>
              )}
            </Group>
          </Stack>
        </SimpleGrid>
      </Card>

      <ConsoleModal vmid={vm.vmid} name={vm.name} opened={consoleOpen} onClose={() => setConsoleOpen(false)} />
      <IsoPickerModal
        vmid={vm.vmid}
        name={vm.name}
        opened={isoOpen}
        onClose={() => setIsoOpen(false)}
        mountedIso={mountedIso}
        onChanged={() => void load()}
      />
      <ResizeVmModal
        vmid={vm.vmid}
        current={{
          cores: vm.cpu_cores,
          memory_mb: vm.memory_mb,
          disk_gb: Math.round(vm.disk_total_bytes / 1024 ** 3),
        }}
        disks={vm.disks}
        bootSlot={vm.boot_slot ?? null}
        opened={resizeOpen}
        onClose={() => setResizeOpen(false)}
        onChanged={() => void load()}
      />
      <AddDiskModal
        vmid={vm.vmid}
        opened={addDiskOpen}
        onClose={() => setAddDiskOpen(false)}
        onMutated={load}
        dataDisks={dataDisks}
        bootSlot={vm.boot_slot ?? null}
        maxExtraDisks={vm.max_extra_disks ?? 0}
      />
      <SnapshotsModal
        opened={snapshotsOpen}
        onClose={() => setSnapshotsOpen(false)}
        kind="vm"
        vmid={vm.vmid}
        name={vm.name}
        onMutated={load}
      />
    </Stack>
  );
}

/**
 * Small resource card with optional usage bar. Used for CPU / Memory /
 * Status panels. `usagePct` (0-100) renders a colored bar; null omits it.
 */
function ResourceCard({
  icon,
  label,
  primary,
  secondary,
  usagePct,
  usageLabel,
  usageColor,
}: {
  icon: React.ReactNode;
  label: string;
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  usagePct?: number | null;
  usageLabel?: string | null;
  usageColor?: "brand" | "accent" | "green";
}) {
  return (
    <Card padding="lg">
      <Stack gap="sm">
        <Group gap="xs" align="center">
          <Text c="dimmed">{icon}</Text>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: "0.04em" }}>
            {label}
          </Text>
        </Group>
        <Stack gap={2}>
          <Text size="xl" fw={700}>
            {primary}
          </Text>
          {secondary && (
            <Text size="sm" c="dimmed">
              {secondary}
            </Text>
          )}
        </Stack>
        {usagePct !== null && usagePct !== undefined && (
          <Stack gap={4} mt={2}>
            <Progress
              value={usagePct}
              size="sm"
              color={usageColor ?? "brand"}
              radius="xs"
              styles={{ section: { transition: "width 400ms ease" } }}
            />
            {usageLabel && (
              <Text size="xs" c="dimmed">
                {usageLabel}
              </Text>
            )}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
